/**
 * Smooth token-reveal pacing for streaming answers (Claude-style).
 *
 * SSE delivers text in bursty chunks; painting each chunk at once reads as
 * "dry" jumps. Instead the streaming renderer keeps a moving cursor (`shown`)
 * and advances it toward the full text length on an animation-frame loop,
 * draining whatever is buffered over ~DRAIN_MS. The result is a steady flow
 * of words — matching how claude.ai paces its stream (their DOM shows plain
 * text nodes growing smoothly, no per-token spans).
 */

// Target time to drain the pending (not yet shown) text, in ms.
export const DRAIN_MS = 300;
// Streaming simulado da resposta que ficou retida (modo compacto): o texto já
// chegou inteiro e é revelado do início a este ritmo (~600 chars em 0,5 s),
// depois volta ao ritmo normal dos deltas.
export const REPLAY_CHARS_PER_MS = 1.2;

// Minimum interval between paint updates, in ms (~20fps — smooth to the eye,
// cheap for the markdown pipeline).
export const PACE_TICK_MS = 50;

// Safety valve for the incomplete-link hold: if the unfinished construct got
// this big it's probably not a citation (e.g. a '[' inside a code block) —
// stop holding and let it render.
export const HOLD_MAX_CHARS = 300;

/**
 * Index to cut the visible text so an INCOMPLETE trailing link/citation never
 * renders half-parsed. During streaming, "[Fonte](https://par..." flickers
 * through raw text -> growing autolink -> citation chip; holding the reveal
 * until the construct closes makes it appear once, already in its final form.
 *
 * Holds:
 *  - a trailing "[..." whose "](...)" hasn't closed yet
 *  - a trailing bare URL that may still be growing (no terminator yet)
 */
export function safeCutIndex(text: string): number {
  let cut = text.length;

  const lastOpen = text.lastIndexOf('[');
  if (lastOpen !== -1) {
    const after = text.slice(lastOpen);
    const closeBr = after.indexOf(']');
    let complete = false;
    if (closeBr !== -1) {
      const rest = after.slice(closeBr + 1);
      // "[nota]" without "(" is a finished construct; "[t](url" isn't until ")"
      if (!rest.startsWith('(')) complete = true;
      else if (rest.includes(')')) complete = true;
    }
    if (!complete && text.length - lastOpen <= HOLD_MAX_CHARS) {
      cut = Math.min(cut, lastOpen);
    }
  }

  const urlTail = /(https?:\/\/[^\s<>)]*)$/.exec(text);
  if (urlTail) {
    const urlStart = text.length - urlTail[1].length;
    if (text.length - urlStart <= HOLD_MAX_CHARS) {
      cut = Math.min(cut, urlStart);
    }
  }

  return cut;
}

/**
 * Next cursor position given the current one, the full text length and the
 * elapsed time since the last update. Pure — unit-tested.
 */
export function nextShownChars(
  shown: number,
  target: number,
  dtMs: number,
  drainMs: number = DRAIN_MS,
): number {
  if (target <= shown) return target; // text shrank (regenerate) or caught up
  const behind = target - shown;
  const step = Math.max(1, Math.ceil(behind * Math.min(1, dtMs / drainMs)));
  return Math.min(target, shown + step);
}
